/**
 * @file driver.ts
 * @module api/apiJSON/driver
 * @description Defines driver API request configuration and payload helpers.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.2.5
 */

import {
  IAddDriver,
  IListDriver,
  IUpdateDriver,
  IUpdateDriverState,
} from "../../types";
export const driverJSON = {
  addDriver: ({
    name,
    email,
    imageReader,
    driverSignatureReader,
    phone,
    phoneCountry,
    paymentOptionList,
    isFreelance,
    warehouse,
    drivingLicenseReader,
    drivingLicenseExpiryDate,
    vfdDocReader,
    vehicleInsuranceReader,
    vehicleInsuranceExpiryDate,
  }: IAddDriver) => {
    let form = new FormData();
    form.append("name", name);
    form.append("email", email);
    form.append("image", imageReader);
    if (driverSignatureReader) {
      form.append("driverSignature", driverSignatureReader);
    }
    form.append("phone", phone);
    form.append("phoneCountry", phoneCountry);
    form.append("isFreelance", isFreelance);

    if (paymentOptionList?.length) {
      paymentOptionList.forEach((option, index) => {
        form.append(`paymentOptionList[${index}][paymentMethod]`, option.paymentMethod || "");
        form.append(`paymentOptionList[${index}][number]`, option.number || "");
        if (option.qrImageReader) {
          form.append(`paymentOptionList[${index}][qrImage]`, option.qrImageReader);
        }
      });
    }

    form.append("warehouse", warehouse.value);

    if (drivingLicenseReader) {
      form.append("drivingLicense", drivingLicenseReader);
      if (drivingLicenseExpiryDate) {
        const dlExpiry =
          drivingLicenseExpiryDate instanceof Date
            ? drivingLicenseExpiryDate.toISOString()
            : drivingLicenseExpiryDate;
        form.append("drivingLicenseExpiryDate", dlExpiry as any);
      }
    }

    if (vfdDocReader) {
      form.append("vfdDoc", vfdDocReader);
    }

    if (vehicleInsuranceReader) {
      form.append("vehicleInsurance", vehicleInsuranceReader);
      if (vehicleInsuranceExpiryDate) {
        const insuranceExpiry =
          vehicleInsuranceExpiryDate instanceof Date
            ? vehicleInsuranceExpiryDate.toISOString()
            : vehicleInsuranceExpiryDate;
        form.append("vehicleInsuranceExpiryDate", insuranceExpiry as any);
      }
    }

    return form;
  },
  listDriver: ({
    limit,
    sortKey,
    sortOrder,
    pageNo,
    searchTerm,
    listType,
    isDeleted,
    warehouse,
  }: IListDriver) => {

    const params: any = {
      limit: limit,
      sortKey: sortKey,
      sortOrder: sortOrder,
      needCount: true,
      pageNo: pageNo,
      searchTerm: searchTerm,
      listType: listType,
      isDeleted: isDeleted
    };
    if (warehouse) {
      params.warehouse = warehouse;
    }
    return params;
  },
  updateStatus: ({ active }: IUpdateDriverState) => {
    return { active: active };
  },
  updateDriver: ({
    name,
    email,
    imageReader,
    driverSignatureReader,
    phone,
    phoneCountry,
    paymentOptionList,
    isFreelance,
    warehouse,
    drivingLicenseReader,
    drivingLicenseExpiryDate,
    vfdDocReader,
    vehicleInsuranceReader,
    vehicleInsuranceExpiryDate,
  }: IUpdateDriver) => {
    let form = new FormData();
    form.append("name", name);
    form.append("email", email);
    form.append("phone", phone);
    form.append("phoneCountry", phoneCountry);
    form.append("image", imageReader);
    if (driverSignatureReader) {
      form.append("driverSignature", driverSignatureReader);
    }
    form.append("isFreelance", isFreelance);

    if (paymentOptionList?.length) {
      paymentOptionList.forEach((option, index) => {
        form.append(`paymentOptionList[${index}][paymentMethod]`, option.paymentMethod || "");
        form.append(`paymentOptionList[${index}][number]`, option.number || "");
        if (option.qrImageReader) {
          form.append(`paymentOptionList[${index}][qrImage]`, option.qrImageReader);
        }
      });
    }
    if (warehouse) {
      form.append("warehouse", warehouse.value);
    }

    if (drivingLicenseReader) {
      form.append("drivingLicense", drivingLicenseReader);
      if (drivingLicenseExpiryDate) {
        const dlExpiry =
          drivingLicenseExpiryDate instanceof Date
            ? drivingLicenseExpiryDate.toISOString()
            : drivingLicenseExpiryDate;
        form.append("drivingLicenseExpiryDate", dlExpiry as any);
      }
    }

    if (vfdDocReader) {
      form.append("vfdDoc", vfdDocReader);
    }

    if (vehicleInsuranceReader) {
      form.append("vehicleInsurance", vehicleInsuranceReader);
      if (vehicleInsuranceExpiryDate) {
        const insuranceExpiry =
          vehicleInsuranceExpiryDate instanceof Date
            ? vehicleInsuranceExpiryDate.toISOString()
            : vehicleInsuranceExpiryDate;
        form.append("vehicleInsuranceExpiryDate", insuranceExpiry as any);
      }
    }
    return form;
  },
};
